import { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '@/core/components/ui/input';
import { Search, X, Loader2 } from 'lucide-react';
import { Button } from '@/core/components/ui/button';

interface TranslationSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  isSearchingServer?: boolean;
  isAllDataLoaded?: boolean;
  debounceMs?: number;
  placeholder?: string;
  className?: string;
}

export function TranslationSearchBar({
  value,
  onChange,
  isSearchingServer = false,
  isAllDataLoaded = false,
  debounceMs = 300,
  placeholder = 'Search by key name...',
  className,
}: TranslationSearchBarProps) {
  const [localValue, setLocalValue] = useState(value);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local value when external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Handle input change with conditional debouncing
  const handleInputChange = useCallback((newValue: string) => {
    setLocalValue(newValue);

    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (isAllDataLoaded) {
      // Instant search when data is loaded locally
      onChange(newValue);
    } else {
      // Debounced search for server-side search
      debounceTimerRef.current = setTimeout(() => {
        onChange(newValue);
      }, debounceMs);
    }
  }, [isAllDataLoaded, onChange, debounceMs]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleClear = useCallback(() => {
    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setLocalValue('');
    onChange('');
  }, [onChange]);

  return (
    <div className={`relative ${className}`}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        value={localValue}
        onChange={(e) => handleInputChange(e.target.value)}
        placeholder={placeholder}
        className="pl-9 pr-9"
      />
      {isSearchingServer && (
        // Wrapper does the centering - animate-spin overrides `transform`, so it can't share the icon with -translate-y-1/2
        <div className="absolute right-10 top-1/2 -translate-y-1/2 pointer-events-none">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}
      {localValue && (
        <Button
          variant="ghost"
          size="sm"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
          onClick={handleClear}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
